import React, { useCallback } from 'react'
import { FormattedMessage } from 'react-intl'
import classnames from 'classnames'
import { intl } from 'lang/intl'

import { TtButton } from '@23/ui-react'

import { type TrayCardAction } from '../../../../../../firebase/model/tray-cards/types/action-card-model'
import {
  useActionCardAnswerCount,
  useActionCardStateMutation,
} from '../../../../../../ActionCards/blocs/useActionCards'
import {
  disableLastActiveCard as disableLastActiveCardFirebase,
  editActionCard as editActionCardFirebase,
} from '../../../../../../firebase/hooks/useTray'

import './ActionCardOverview.scss'

interface ActionCardBodyProps {
  card: TrayCardAction
}

const ActionCardOverview = ({ card }: ActionCardBodyProps) => {
  const answerCount = useActionCardAnswerCount(card.actionCardId)

  const { mutateAsync: changeActionCardStateAPI } = useActionCardStateMutation(
    card.actionCardId
  )
  const [isLoading, setIsLoading] = React.useState(false)

  const handleBtnClick = useCallback(async () => {
    setIsLoading(true)
    try {
      const actionCard = await changeActionCardStateAPI(!card.isActive)
      if (!actionCard) return
      if (actionCard.isActive) {
        await disableLastActiveCardFirebase()
      }
      await editActionCardFirebase(card.id, actionCard)
    } catch (error) {
      // handle error
    } finally {
      setIsLoading(false)
    }
  }, [card.id, card.isActive, changeActionCardStateAPI])

  return (
    <div className="tray-action-card-body">
      {!card.title && !card.description && card.image && (
        <img
          src={card.image}
          className="tray-action-card-image"
          alt={intl.formatMessage({
            defaultMessage: 'Action Card Preview Image',
          })}
        />
      )}
      <div className="tray-action-card-grid">
        {card.title && (
          <>
            <span className="tray-action-card-label">
              <FormattedMessage defaultMessage="Title:" />
            </span>
            <span className="tray-action-card-value">{card.title}</span>
          </>
        )}
        {card.description && (
          <>
            <span className="tray-action-card-label">
              <FormattedMessage defaultMessage="Text:" />
            </span>
            <div className="tray-action-card-value">
              <div className="tray-action-card-description">
                {card.description}
              </div>
            </div>
          </>
        )}

        {card.options && card.options.length > 0 && (
          <>
            <span className="tray-action-card-label">
              <FormattedMessage defaultMessage="Fields:" />
            </span>
            <div className="tray-action-card-options-list">
              {card.options.map((el) => (
                <div key={el.id} className="tray-action-card-value">
                  {el.label}
                </div>
              ))}
            </div>
          </>
        )}
        {card.cta && card.cta.text && (
          <>
            <span className="tray-action-card-label">
              <FormattedMessage defaultMessage="Button:" />
            </span>
            <span
              className={classnames(
                'tray-action-card-cta-value',
                'tray-action-card-value'
              )}
            >
              {card.cta.text}
            </span>
          </>
        )}
        {answerCount > 0 && (
          <>
            <div className="divider" />
            <span className="tray-action-card-label">
              <FormattedMessage defaultMessage="Answers:" />
            </span>
            <span className="tray-action-card-answer-count">{answerCount}</span>
          </>
        )}
      </div>
      <TtButton
        className="tray-action-card-btn"
        color="secondary"
        size="small"
        variant="default"
        pill
        disabled={isLoading}
        onClick={handleBtnClick}
      >
        {card.isActive ? (
          <FormattedMessage defaultMessage="Close" />
        ) : (
          <FormattedMessage defaultMessage="Start" />
        )}
      </TtButton>
    </div>
  )
}

export { ActionCardOverview }
